<template>
  <a-table
    class="table datatable thead-light dq-report-table"
    :columns="columns"
    :data-source="dataSource"
    :loading="loading"
    :pagination="pagination"
    @change="onChange"
    row-key="key"
  >
    <template #bodyCell="{ column, record }">
      <template v-if="column.key === 'Name'">
        <div class="d-flex align-items-center">
          <a
            href="javascript:void(0);"
            data-bs-toggle="modal"
            data-bs-target="#view_details"
          >
            <DqAvatar
              :src="record.Image"
              :name="record.name"
              size-class="avatar-md"
              shape-class="rounded-circle"
            />
          </a>
          <div class="ms-2">
            <p class="text-dark mb-0">
              <a href="javascript:void(0);" data-bs-toggle="modal" data-bs-target="#view_details">
                {{ record.name }}
              </a>
            </p>
            <span class="fs-12">{{ record.role }}</span>
          </div>
        </div>
      </template>

      <template v-else-if="column.key === 'Paid_Amount'">
        {{ currency }}
        {{
          Number(record.paid_amount || 0).toLocaleString(undefined, {
            minimumFractionDigits: 2,
          })
        }}
      </template>

      <template v-else-if="column.key === 'Paid_Month'">
        {{ record.period_label }}
      </template>

      <template v-else-if="column.key === 'Frequency'">
        {{ record.frequency }}
      </template>

      <template v-else-if="column.key === 'Status'">
        <span
          class="badge"
          :class="
            record.payslip_status === 'emailed'
              ? 'badge-soft-success'
              : record.payslip_status === 'archive' || record.historical
              ? 'badge-soft-info'
              : record.payslip_id
              ? 'badge-soft-primary'
              : 'badge-soft-warning'
          "
        >
          {{
            record.payslip_status ||
            (record.historical ? "archive" : record.payslip_id ? "generated" : "pending")
          }}
        </span>
      </template>

      <template v-else-if="column.key === 'Action'">
        <button
          class="btn btn-sm btn-primary"
          :disabled="
            (!record.payslip_id && !record.historical) ||
            downloadingId === (record.payslip_id || record.download_key)
          "
          @click="downloadPayslip(record)"
        >
          <span
            v-if="downloadingId === (record.payslip_id || record.download_key)"
            class="spinner-border spinner-border-sm me-1"
          ></span>
          Download
        </button>
      </template>
    </template>
  </a-table>
</template>


<script setup>
import { computed, onMounted, ref } from "vue";
import DqAvatar from "@/components/shared/DqAvatar.vue";
import payrollService from "@/services/payroll.service";
import reportsService from "@/services/reports.service";
import { toast } from "@/utils/toast";

/**
 * Props:
 *  - rows: [{ name, role, avatar_url, net_pay, month_label, year, ... }]
 *  - loading: boolean
 *  - currency: "KES" | "USD" | ...
 *  - total: number (server total)
 *  - pageSize: number
 *  - current: number
 *
 * Emits:
 *  - change(pagination)  // Antd controlled pagination event
 */
const props = defineProps({
  rows: { type: Array, default: () => [] },
  loading: { type: Boolean, default: false },
  currency: { type: String, default: "KES" },
  total: { type: Number, default: 0 },
  pageSize: { type: Number, default: 25 },
  current: { type: Number, default: 1 },
});
const emit = defineEmits(["change"]);

const columns = [
  { sorter: false }, // avatar col
  {
    title: "Name",
    dataIndex: "name",
    key: "Name",
    sorter: (a, b) => (a.name || "").localeCompare(b.name || ""),
  },
  {
    title: "Paid Amount",
    dataIndex: "paid_amount",
    key: "Paid_Amount",
    sorter: (a, b) => (a.paid_amount || 0) - (b.paid_amount || 0),
  },
  {
    title: "Pay Period",
    dataIndex: "period_label",
    key: "Paid_Month",
    sorter: (a, b) => (a.period_label || "").localeCompare(b.period_label || ""),
  },
  {
    title: "Frequency",
    dataIndex: "frequency",
    key: "Frequency",
    sorter: (a, b) => (a.frequency || "").localeCompare(b.frequency || ""),
  },
  {
    title: "Status",
    dataIndex: "payslip_status",
    key: "Status",
  },
  {
    title: "Action",
    key: "Action",
  },
];

const downloadingId = ref(null);

const dataSource = computed(() =>
  (props.rows || []).map((r, idx) => ({
    key: r.id || idx,
    Image: r?.employee?.avatar_url || "",
    name:
      r.name ||
      `${r?.employee?.first_name || ""} ${r?.employee?.last_name || ""}`.trim(),
    role: r.role || r?.employee?.designation?.name || "-",
    paid_amount: r.net_pay ?? r.paid_amount ?? 0,
    period_label: r.period_label || r.paid_month || r.period || "-",
    period: r.period || null,
    frequency: r.frequency || "monthly",
    payslip_id: r?.payslip?.id || null,
    payslip_status: r?.payslip?.status || null,
    historical: !!(r.historical || r.source === "imported_history"),
    employee_db_id: r.employee_db_id || r?.employee?.id || null,
    download_key: r.historical
      ? `hist-${r.employee_db_id || r?.employee?.id}-${r.period}`
      : r?.payslip?.id || null,
  }))
);

const pagination = computed(() => ({
  total: props.total,
  pageSize: props.pageSize,
  current: props.current,
  showTotal: (t) => `Total ${t} items`,
}));

function onChange(pagination, filters, sorter) {
  emit("change", pagination, filters, sorter);
}

async function downloadPayslip(record) {
  if (!record?.payslip_id && !record?.historical) return;

  try {
    downloadingId.value = record.payslip_id || record.download_key;
    const res = record.historical
      ? await reportsService.downloadHistoricalPayslip({
          employee_id: record.employee_db_id,
          period: record.period,
        })
      : await payrollService.download(record.payslip_id);
    const blob = new Blob([res.data], { type: "application/pdf" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = record.historical
      ? `payslip-archive-${record.period}.pdf`
      : `payslip-${record.payslip_id}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  } catch (err) {
    toast(err?.response?.data?.message || "Failed to download payslip.", "error");
  } finally {
    downloadingId.value = null;
  }
}

onMounted(() => {
  //dataSource

  console.log("DataSource:", props.rows);
});         
</script>

<style scoped>
@media (max-width: 767.98px) {
  :deep(.dq-report-table .ant-table) {
    min-width: 900px;
  }

  :deep(.dq-report-table .ant-table-cell) {
    vertical-align: top;
    white-space: normal;
  }
}
</style>


